import _ from 'lodash';
import React, { useState, useRef } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import * as yup from 'yup';

import { backgroundImage } from 'utils/media';
import { pluralize } from 'utils/utils';
import { ServiceTypes, Email } from 'constants/constants';

import Channel from 'store/models/Channel';
import Stream from 'store/models/Stream';
import Session from 'store/models/Session';
import Twitch, { selectTwitchSearch } from 'store/models/Twitch';
import YouTube, { selectYouTubeSearch } from 'store/models/YouTube';
import Account, { selectCurrentAccount, canAddChannel, isFreeAccount } from 'store/models/Account';

import Modal, { ModalContent, ModalHeader, ModalTitle, ModalSubtitle, ModalActions, ModalNotice } from 'components/Modal';
import TextInput from 'components/forms/TextInput';
import SearchInput from 'components/forms/SearchInput';
import { ErrorList } from 'components/forms/Error';
import Button, { XIconButton, SaveButton, DeleteIconButton } from 'components/Button';
import { SelectButtons, SelectButtonItem } from 'components/forms/SelectButtons';
import * as Icons from 'components/Svg';



// ======================
// STORE
// ====================== 

const selector = state => {
  return {
    account: selectCurrentAccount(state),
    twitchUser: selectTwitchSearch(state),
    youtubeUser: selectYouTubeSearch(state),
  };
}

// ======================
// COMPONENT
// ====================== 

const AddChannelModal = ({ closeModal,...props }) => {
  const dispatch = useDispatch();
  let { account, twitchUser, youtubeUser } = useSelector(selector);
  
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [service, setService] = useState(ServiceTypes.TWITCH);
  const [searching, setSearching] = useState(false);
  const [didSearch, setDidSearch] = useState(false);
  const [channels, setChannels] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const searchInputRef = useRef();
  const canAdd = canAddChannel(account);
  const isFree = isFreeAccount(account);
  const hasChannels = !_.isEmpty(channels);
  
  const onInputChange = (value) => {
    setSearch(value);
    setResults([]);
    setDidSearch(false);
  }

  const onServiceChange = val => {
    setService(val);
    setSearch('');
    setDidSearch(false);
    setSearching(false);
    setResults([]);
    setChannels([]);
    searchInputRef.current?.getInput()?.focus();
  }

  const onSearch = async (value) => {
    if(searching)
      return;

    try {
      setSearching(true);
      let results = service === ServiceTypes.TWITCH
        ? await dispatch(Twitch.actions.searchUsers(value))
        : await dispatch(YouTube.actions.channelFromUrl(value));
      
      setSearching(false);
      setDidSearch(false);
      setResults(results.body?.results |> _.take(#, 4));
    } catch(err) {
      setDidSearch(true);
      setSearching(false);
    }
    searchInputRef.current?.getInput()?.focus();
  };

  const onSearchCancel = async () => {
    setSearching(false);
    setResults([]);
  }

  const onResultSelect = channel => {
    setChannels(channels 
      |> _.concat(#, channel) 
      |> _.uniqBy(#, 'serviceId'));
    setResults(null);
    setSearch('');
  }

  const onRemoveClick = (e,idx) => {
    e.preventDefault();
    e.stopPropagation();
    setChannels(_.without(channels, channels[idx]));
    searchInputRef.current?.getInput()?.focus();
  }

  const onSaveClick = async e => {
    if(_.isEmpty(channels))
      return closeModal();

    setSaving(true);
    if(service === ServiceTypes.TWITCH) {
      await dispatch(Channel.actions.addTwitchChannels(_.map(channels, 'serviceId' )))
    } else if(service === ServiceTypes.YOUTUBE) {
      await dispatch(Channel.actions.addYouTubeChannels(_.map(channels, 'serviceId' )))
    }

    await dispatch(Channel.actions.fetchChannels());
    await dispatch(Stream.actions.fetchLiveStreams());
    await dispatch(Session.actions.fetchRunningSessions()),

    setSaving(false);
    closeModal();
  }

  const renderSearchResult = item => (
    <span className="search-result">
      <span className="avatar" style={backgroundImage(item.imageUrl)}></span>
      <span className="username">{ item.username }</span>
    </span>)

  let label = service === ServiceTypes.TWITCH
    ? `Find By Twitch Username`
    : `Find By YouTube Channel URL`;

  let placeholder = service === ServiceTypes.TWITCH
    ? `Enter A Username`
    : `Paste A Channel URL Here`;
  
  return (
    <Modal className="add-channel" size="m">
      <ModalHeader>
        <ModalTitle>Add A Channel</ModalTitle>
        <ModalSubtitle>
          Add streamers to your account to manage their slides and track their stats.
        </ModalSubtitle>
      </ModalHeader>
      { !canAdd && <>
        <ModalContent>
          <ModalNotice className="free-account">
            You have a free account, which limits you to one channel. To add more channels, please email <strong>{Email.SALES}</strong> and upgrade to a premium account.
          </ModalNotice>
        </ModalContent> 
        <ModalActions>
            <Button onClick={closeModal}>Ok</Button>
          </ModalActions>
      </>}
      { canAdd && <>
        <ModalContent>
          <SelectButtons onChange={onServiceChange} value={service}>
            <SelectButtonItem name={ServiceTypes.TWITCH} value={ServiceTypes.TWITCH}><Icons.TwitchIcon color="white" size="s" /> Twitch</SelectButtonItem>
            <SelectButtonItem name={ServiceTypes.YOUTUBE} value={ServiceTypes.YOUTUBE}><Icons.YouTubeIcon size="m" /> YouTube</SelectButtonItem>
          </SelectButtons>
          <form onSubmit={onSearch}>
            {!(isFree && hasChannels) && 
            <SearchInput
              ref={searchInputRef}
              focused={true}
              className="search"
              name="search"
              align="center"
              label={label}
              value={search}
              results={results}
              loading={searching}
              disabled={searching}
              placeholder={placeholder}
              renderItem={renderSearchResult}
              onChange={onInputChange}
              onSearch={onSearch}
              onCancel={onSearchCancel}
              onItemSelect={onResultSelect} />}
            
            { didSearch && (_.isNil(twitchUser) || _.isNil(youtubeUser)) && 
            <ErrorList className="not-found" errors={['No Channels Found. Please try another search']} />}
          </form>
          { hasChannels &&
          <div className="channel-list">
            { _.map(channels, (c, i) => (
              <div key={c.username} className="channel-result">
                <div>
                  <div className="avatar" style={backgroundImage(c.imageUrl)}></div>
                  <div className="username">{ c.username }</div>
                </div>
                <DeleteIconButton onClick={e=>onRemoveClick(e,i)} />
              </div>   
            )) }
            </div>}
        </ModalContent> 
        <ModalActions>
          <SaveButton onClick={onSaveClick} disabled={saving || _.size(channels) === 0} size="l">
            { _.size(channels) === 0 ? 'Add' : `Add ${_.size(channels)} ${pluralize('Channel', _.size(channels))}`}
          </SaveButton>
        </ModalActions>
      </>}
    </Modal>);
}

export default AddChannelModal


